import type { Editor } from '@tiptap/core';
import type { Node } from '@tiptap/pm/model';
import {
  type EditorState,
  NodeSelection,
  TextSelection,
  type Transaction,
} from '@tiptap/pm/state';

const TOP_LEVEL_DEPTH = 1;

// Check if a node is atomic (leaf node that can't contain text cursor).
// Examples: images, cards, tables
const isAtomicNode = (node: Node): boolean => {
  return node.isAtom || node.type.spec.atom || false;
};

// Set selection on the moved block, maintaining cursor position for text blocks
// and selecting the entire node for atomic blocks.
const setSelectionAfterMove = (
  transaction: Transaction,
  node: Node,
  movedBlockPos: number,
  selectionOffset: number,
): void => {
  if (isAtomicNode(node)) {
    // Select the entire node for atomic blocks
    try {
      transaction.setSelection(
        NodeSelection.create(transaction.doc, movedBlockPos),
      );
    } catch (e) {
      // Fallback to text selection if node selection fails
      transaction.setSelection(
        TextSelection.near(transaction.doc.resolve(movedBlockPos + 1)),
      );
    }
  } else {
    // For text blocks, maintain the cursor position within the block
    const newSelectionPos = movedBlockPos + selectionOffset;

    const safePos = Math.max(
      movedBlockPos + 1,
      Math.min(newSelectionPos, movedBlockPos + node.nodeSize - 1),
    );

    try {
      transaction.setSelection(
        TextSelection.near(transaction.doc.resolve(safePos)),
      );
    } catch (e) {
      transaction.setSelection(
        TextSelection.near(transaction.doc.resolve(movedBlockPos + 1)),
      );
    }
  }
};

export interface TopLevelBlockInfo {
  pos: number;
  node: Node;
  isFirst: boolean;
  isLast: boolean;
  blockIndex: number;
}

export const findTopLevelBlockInfo = (
  state: EditorState,
): TopLevelBlockInfo | null => {
  const { $from } = state.selection;

  // Walk up from selection to find top-level block
  for (let depth = $from.depth; depth > 0; depth--) {
    if (depth === TOP_LEVEL_DEPTH) {
      const node = $from.node(depth);
      const pos = $from.before(depth);

      // Find block index among siblings
      const parent = $from.node(0);
      let blockIndex = 0;
      let currentPos = 0;

      for (let i = 0; i < parent.childCount; i++) {
        const child = parent.child(i);
        if (currentPos === pos) {
          blockIndex = i;
          break;
        }
        currentPos += child.nodeSize;
      }

      return {
        pos,
        node,
        isFirst: blockIndex === 0,
        isLast: blockIndex === parent.childCount - 1,
        blockIndex,
      };
    }
  }

  return null;
};

export const findTopLevelBlockInfoByPos = (
  state: EditorState,
  pos: number,
): TopLevelBlockInfo | null => {
  const parent = state.doc;

  // Validate position is within document bounds
  if (pos < 0 || pos >= parent.content.size) {
    return null;
  }

  // Find the block at this position
  let currentPos = 0;
  for (let i = 0; i < parent.childCount; i++) {
    const child = parent.child(i);
    // Check if position is at the start of this block
    if (currentPos === pos) {
      return {
        pos,
        node: child,
        isFirst: i === 0,
        isLast: i === parent.childCount - 1,
        blockIndex: i,
      };
    }
    currentPos += child.nodeSize;
  }

  return null;
};

export const swapWithPreviousBlock = (
  editor: Editor,
  pos: number,
  node: Node,
  blockIndex: number,
): void => {
  const { state } = editor;
  const parent = state.doc;

  if (blockIndex === 0) return;

  const selectionOffset = state.selection.from - pos;

  const prevNode = parent.child(blockIndex - 1);
  const prevPos = pos - prevNode.nodeSize;

  const { tr: transaction } = state;

  transaction.delete(pos, pos + node.nodeSize);
  transaction.delete(prevPos, prevPos + prevNode.nodeSize);

  transaction.insert(prevPos, node);
  transaction.insert(prevPos + node.nodeSize, prevNode);

  setSelectionAfterMove(transaction, node, prevPos, selectionOffset);

  editor.view.dispatch(transaction);
  editor.view.focus();
};

export const swapWithNextBlock = (
  editor: Editor,
  pos: number,
  node: Node,
  blockIndex: number,
): void => {
  const { state } = editor;
  const parent = state.doc;

  if (blockIndex >= parent.childCount - 1) return;

  const selectionOffset = state.selection.from - pos;
  const nextNode = parent.child(blockIndex + 1);

  const { tr: transaction } = state;

  transaction.delete(
    pos + node.nodeSize,
    pos + node.nodeSize + nextNode.nodeSize,
  );
  transaction.delete(pos, pos + node.nodeSize);

  transaction.insert(pos, nextNode);
  transaction.insert(pos + nextNode.nodeSize, node);

  const movedBlockPos = pos + nextNode.nodeSize;
  setSelectionAfterMove(transaction, node, movedBlockPos, selectionOffset);

  editor.view.dispatch(transaction);
  editor.view.focus();
};
